import React, { useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    Container,
    Typography,
    Paper,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    Chip,
    Skeleton,
    Alert,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    ChevronRight as ChevronRightIcon,
    Instagram as InstagramIcon,
    OpenInNew as OpenInNewIcon,
    SwipeLeft as SwipeLeftIcon,
} from '@mui/icons-material';
import TikTokIcon from '../components/icons/TikTokIcon';
import ShortFormDetailLayout from '../components/ShortFormDetailLayout';
import apiService from '../services/api';
import { ROUTES_NAMES } from '../components/Layout';
import { useBrandExplorerStore } from '../stores/brandExplorerStore';
import { useTrackerStore } from '../stores/trackerStore';
import { useAuth } from '../contexts/AuthContext';
import { getS3Url, ensureDirectS3Url } from '../utils/imageUtils';

const TONYBET_BRAND_ID = '68ab4884dfd4c581cbe669bb';

type ShortFormPlatform = 'instagram' | 'youtube' | 'tiktok';

interface BrandExplorerShortFormPlatformDetailProps {
    fixedBrandId?: string;
}

const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('es-ES').format(num);
};

const extractTikTokUsername = (url: string): string | null => {
    const match = url?.match(/tiktok\.com\/@([^/]+)/);
    return match ? match[1] : null;
};

function buildCreatorsFromMentions(mentions: any[], platformType: 'instagram' | 'tiktok') {
    const creatorMap = new Map<string, {
        creatorId: string;
        username: string;
        fullName?: string;
        profilePicUrl?: string;
        profilePicS3Key?: string;
        isVerified?: boolean;
        followersCount?: number;
        platform?: 'instagram' | 'tiktok';
        reels: any[];
        totalReels: number;
        totalViews: number;
        totalLikes: number;
        totalComments: number;
    }>();
    mentions.forEach((mention) => {
        const source = mention.content?.source;
        let username: string | undefined;
        let creatorId: string;
        let fullName: string | undefined;
        let profilePicUrl: string | undefined;
        let profilePicS3Key: string | undefined;
        let isVerified: boolean | undefined;
        let followersCount: number | undefined;
        if (source === 'instagram') {
            const owner = mention.content?.insights?.owner;
            const accountMetadata = mention.content?.insights?.accountMetadata;
            if (!owner?.username) return;
            username = owner.username;
            creatorId = owner.username;
            fullName = owner.fullName;
            profilePicUrl = accountMetadata?.profilePicUrl;
            profilePicS3Key = (accountMetadata as any)?.profilePicS3Key;
            isVerified = owner.isVerified;
            followersCount = accountMetadata?.followersCount;
        } else if (source === 'tiktok') {
            const owner = mention.content?.insights?.owner;
            const accountMetadata = mention.content?.insights?.accountMetadata;
            username = owner?.username || extractTikTokUsername(mention.content?.url || '') || undefined;
            if (!username) return;
            creatorId = username;
            fullName = owner?.fullName;
            profilePicUrl = accountMetadata?.profilePicUrl;
            profilePicS3Key = (accountMetadata as any)?.profilePicS3Key;
            isVerified = owner?.isVerified;
            followersCount = accountMetadata?.followersCount;
        } else return;
        const existing = creatorMap.get(creatorId);
        const views = mention.content?.views || 0;
        const likes = mention.content?.likes || 0;
        const comments = mention.content?.comments || 0;
        if (existing) {
            existing.reels.push(mention);
            existing.totalReels += 1;
            existing.totalViews += views;
            existing.totalLikes += likes;
            existing.totalComments += comments;
            if (profilePicS3Key && !existing.profilePicS3Key) existing.profilePicS3Key = profilePicS3Key;
        } else {
            creatorMap.set(creatorId, {
                creatorId,
                username: username!,
                fullName,
                profilePicUrl,
                profilePicS3Key,
                isVerified,
                followersCount,
                platform: platformType,
                reels: [mention],
                totalReels: 1,
                totalViews: views,
                totalLikes: likes,
                totalComments: comments,
            });
        }
    });
    return Array.from(creatorMap.values()).sort((a, b) => b.totalViews - a.totalViews);
}

const BrandExplorerShortFormPlatformDetail: React.FC<BrandExplorerShortFormPlatformDetailProps> = ({ fixedBrandId }) => {
    const { platform } = useParams<{ platform: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { selectedBrand, startDate: explorerStartDate, endDate: explorerEndDate } = useBrandExplorerStore();
    const { startDate: trackerStartDate, endDate: trackerEndDate } = useTrackerStore();
    const { getCompanyTrackerBrandId } = useAuth();

    const isTracker = location.pathname.startsWith('/tracker');
    const isEnglish = !!fixedBrandId;

    const brandIdParam = searchParams.get('brandId');
    const startDateParam = isTracker ? trackerStartDate : (searchParams.get('startDate') || explorerStartDate);
    const endDateParam = isTracker ? trackerEndDate : (searchParams.get('endDate') || explorerEndDate);

    const brandId = fixedBrandId || (isTracker ? getCompanyTrackerBrandId() : null) || selectedBrand?._id || brandIdParam || null;
    const effectivePlatform = (platform === 'instagram' || platform === 'youtube' || platform === 'tiktok') ? platform : null;

    const { data: brandData, isLoading, error } = useQuery({
        queryKey: ['brand-data', brandId, startDateParam, endDateParam],
        queryFn: () => apiService.getBrandData(
            brandId!,
            undefined,
            undefined,
            startDateParam,
            endDateParam
        ),
        enabled: !!brandId,
        staleTime: 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
    });

    const shortFormMentions = useMemo(() => {
        if (!brandData?.periods) return [];
        const filterMentions = (mention: any) => {
            const isShort = (mention.contentForm || 'short') === 'short';
            if (!isShort) return false;
            const date = new Date(mention.content?.publishedAt);
            if (fixedBrandId) return date.getMonth() === 0 && date.getFullYear() === 2026;
            if (startDateParam && endDateParam) {
                const dateStr = date.toISOString().slice(0, 10);
                return dateStr >= startDateParam && dateStr <= endDateParam;
            }
            return true;
        };
        return brandData.periods.flatMap((y: any) => y.mentions).filter(filterMentions);
    }, [brandData, fixedBrandId, startDateParam, endDateParam]);

    const platformMentions = useMemo(() => {
        if (!effectivePlatform) return [];
        if (effectivePlatform === 'instagram') return shortFormMentions.filter((m: any) => m.content?.source === 'instagram' && m.content?.mediaType === 'reel');
        if (effectivePlatform === 'youtube') return shortFormMentions.filter((m: any) => m.content?.source === 'youtube' && m.content?.mediaType === 'short');
        if (effectivePlatform === 'tiktok') return shortFormMentions.filter((m: any) => m.content?.source === 'tiktok');
        return [];
    }, [shortFormMentions, effectivePlatform]);

    const creatorsData = useMemo(() => {
        const periods = brandData?.periods ?? [];
        if (effectivePlatform === 'instagram') {
            const fromBackend = periods.flatMap((p: any) => (p.shortFormByCreator?.instagram ?? []));
            if (fromBackend.length > 0) {
                return fromBackend.map((c: any) => ({
                    creatorId: c.creatorId,
                    username: c.username,
                    profilePicUrl: c.profilePicUrl,
                    profilePicS3Key: c.profilePicS3Key,
                    totalReels: c.totalReels ?? 0,
                    totalViews: c.totalViews ?? 0,
                    totalLikes: c.totalLikes ?? 0,
                    totalComments: c.totalComments ?? 0,
                    reels: [],
                }));
            }
            return buildCreatorsFromMentions(platformMentions, 'instagram');
        }
        if (effectivePlatform === 'tiktok') {
            const fromBackend = periods.flatMap((p: any) => (p.shortFormByCreator?.tiktok ?? []));
            if (fromBackend.length > 0) {
                return fromBackend.map((c: any) => ({
                    creatorId: c.creatorId,
                    username: c.username,
                    profilePicUrl: c.profilePicUrl,
                    profilePicS3Key: c.profilePicS3Key,
                    totalReels: c.totalReels ?? 0,
                    totalViews: c.totalViews ?? 0,
                    totalLikes: c.totalLikes ?? 0,
                    totalComments: c.totalComments ?? 0,
                    reels: [],
                }));
            }
            return buildCreatorsFromMentions(platformMentions, 'tiktok');
        }
        return [];
    }, [brandData?.periods, effectivePlatform, platformMentions]);

    // YouTube: channels from youtubeSection.youtubeShortsByAccount (same layout as creators for IG/TikTok)
    const youtubeChannelsData = useMemo(() => {
        if (effectivePlatform !== 'youtube') return [];
        const periods = brandData?.periods ?? [];
        const byChannel = new Map<string, { channelId: string; channelName: string; imageUrl?: string; imageS3Key?: string; totalShorts: number; totalViews: number; totalLikes: number; totalComments: number }>();
        periods.forEach((p: any) => {
            (p.youtubeSection?.youtubeShortsByAccount ?? []).forEach((a: any) => {
                const id = a.channelId ?? '';
                const existing = byChannel.get(id);
                if (existing) {
                    existing.totalShorts += a.totalShorts ?? 0;
                    existing.totalViews += a.totalViews ?? 0;
                    existing.totalLikes += a.totalLikes ?? 0;
                    existing.totalComments += a.totalComments ?? 0;
                } else {
                    byChannel.set(id, {
                        channelId: id,
                        channelName: a.channelName ?? 'YouTube Channel',
                        imageUrl: a.imageUrl,
                        imageS3Key: a.imageS3Key,
                        totalShorts: a.totalShorts ?? 0,
                        totalViews: a.totalViews ?? 0,
                        totalLikes: a.totalLikes ?? 0,
                        totalComments: a.totalComments ?? 0,
                    });
                }
            });
        });
        return Array.from(byChannel.values()).sort((a, b) => b.totalViews - a.totalViews);
    }, [brandData?.periods, effectivePlatform]);

    const platformMetrics = useMemo(() => {
        if (effectivePlatform === 'youtube') {
            if (youtubeChannelsData.length > 0) {
                return {
                    totalCreators: youtubeChannelsData.length,
                    totalReels: youtubeChannelsData.reduce((s, c) => s + c.totalShorts, 0),
                    totalViews: youtubeChannelsData.reduce((s, c) => s + c.totalViews, 0),
                    totalLikes: youtubeChannelsData.reduce((s, c) => s + c.totalLikes, 0),
                    totalComments: youtubeChannelsData.reduce((s, c) => s + c.totalComments, 0),
                };
            }
            const n = platformMentions.length;
            if (n === 0) return null;
            const totalViews = platformMentions.reduce((s: number, m: any) => s + (m.content?.views ?? 0), 0);
            const totalLikes = platformMentions.reduce((s: number, m: any) => s + (m.content?.likes ?? 0), 0);
            const totalComments = platformMentions.reduce((s: number, m: any) => s + (m.content?.comments ?? 0), 0);
            return { totalCreators: 0, totalReels: n, totalViews, totalLikes, totalComments };
        }
        const data = creatorsData as Array<{ totalReels: number; totalViews: number; totalLikes: number; totalComments: number }>;
        if (data.length === 0) return null;
        return {
            totalCreators: data.length,
            totalReels: data.reduce((s, c) => s + c.totalReels, 0),
            totalViews: data.reduce((s, c) => s + c.totalViews, 0),
            totalLikes: data.reduce((s, c) => s + c.totalLikes, 0),
            totalComments: data.reduce((s, c) => s + c.totalComments, 0),
        };
    }, [effectivePlatform, platformMentions, creatorsData, youtubeChannelsData]);

    const handleBack = () => {
        const baseRoute = fixedBrandId ? ROUTES_NAMES.TONYBET_EXPLORER_YEAR : ROUTES_NAMES.TRACKER;
        if (fixedBrandId || isTracker) {
            navigate(baseRoute);
        } else {
            const params = new URLSearchParams();
            if (brandId) params.set('brandId', brandId);
            if (startDateParam) params.set('startDate', startDateParam);
            if (endDateParam) params.set('endDate', endDateParam);
            navigate(`${baseRoute}?${params.toString()}`);
        }
    };

    const handleCreatorClick = (creatorId: string) => {
        const creatorRoute = fixedBrandId ? ROUTES_NAMES.TONYBET_EXPLORER_CREATOR : ROUTES_NAMES.TRACKER_CREATOR;
        const route = fixedBrandId || isTracker
            ? creatorRoute.replace(':platform', effectivePlatform === 'instagram' || effectivePlatform === 'tiktok' ? effectivePlatform : 'instagram').replace(':creatorId', creatorId)
            : creatorRoute.replace(':creatorId', creatorId);
        if (fixedBrandId || isTracker) {
            navigate(route);
        } else {
            const params = new URLSearchParams();
            if (selectedBrand) params.set('brandId', selectedBrand._id);
            if (startDateParam) params.set('startDate', startDateParam);
            if (endDateParam) params.set('endDate', endDateParam);
            navigate(`${route}?${params.toString()}`);
        }
    };

    const handleContentClick = (contentId: string, content: any, contentMentions: any[]) => {
        const contentRoute = fixedBrandId ? ROUTES_NAMES.TONYBET_SHORTFORM_CONTENT : ROUTES_NAMES.TRACKER_SHORTFORM_CONTENT;
        const route = contentRoute.replace(':contentId', contentId);
        if (fixedBrandId || isTracker) {
            navigate(route, { state: { content, mentions: contentMentions } });
        } else {
            const params = new URLSearchParams();
            if (selectedBrand) params.set('brandId', selectedBrand._id);
            if (startDateParam) params.set('startDate', startDateParam);
            if (endDateParam) params.set('endDate', endDateParam);
            navigate(`${route}?${params.toString()}`, { state: { content, mentions: contentMentions } });
        }
    };

    const platformLabels: Record<ShortFormPlatform, { title: string; titleEn: string }> = {
        instagram: { title: 'Instagram Reels', titleEn: 'Instagram Reels' },
        youtube: { title: 'YouTube Shorts', titleEn: 'YouTube Shorts' },
        tiktok: { title: 'TikTok Videos', titleEn: 'TikTok Videos' },
    };

    if (!effectivePlatform) {
        return (
            <Container maxWidth="xl" disableGutters sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
                <IconButton onClick={handleBack}><ArrowBackIcon /></IconButton>
                <Alert severity="error">{isEnglish ? 'Invalid platform.' : 'Plataforma no válida.'}</Alert>
            </Container>
        );
    }

    if (isLoading && !brandData) {
        return (
            <Container maxWidth="xl" disableGutters sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
                <Skeleton variant="rectangular" height={48} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={120} sx={{ mb: 2 }} />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="xl" disableGutters sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
                <IconButton onClick={handleBack}><ArrowBackIcon /></IconButton>
                <Alert severity="error">{isEnglish ? 'Failed to load data.' : 'Error al cargar los datos.'}</Alert>
            </Container>
        );
    }

    const label = isEnglish ? platformLabels[effectivePlatform].titleEn : platformLabels[effectivePlatform].title;
    const contentCountLabel = effectivePlatform === 'youtube' ? 'Shorts' : effectivePlatform === 'tiktok' ? (isEnglish ? 'Videos' : 'Videos') : 'Reels';
    const firstCreator = effectivePlatform === 'youtube' ? null : creatorsData[0];
    const firstYouTubeChannel = effectivePlatform === 'youtube' ? youtubeChannelsData[0] : null;
    const headerAvatar = (() => {
        if (firstCreator) {
            const src = firstCreator.profilePicS3Key ? ensureDirectS3Url(getS3Url(firstCreator.profilePicS3Key)) : firstCreator.profilePicUrl ?? null;
            return src ? <Avatar src={src} sx={{ width: 56, height: 56 }} /> : <Avatar sx={{ width: 56, height: 56 }}>{effectivePlatform === 'tiktok' ? <TikTokIcon /> : <InstagramIcon />}</Avatar>;
        }
        if (firstYouTubeChannel) {
            const src = firstYouTubeChannel.imageS3Key ? ensureDirectS3Url(getS3Url(firstYouTubeChannel.imageS3Key)) : firstYouTubeChannel.imageUrl ?? null;
            return src ? <Avatar src={src} sx={{ width: 56, height: 56 }} /> : <Avatar sx={{ width: 56, height: 56 }}>{firstYouTubeChannel.channelName?.charAt(0)?.toUpperCase() ?? '?'}</Avatar>;
        }
        return <Avatar sx={{ width: 56, height: 56 }}>{label.charAt(0)}</Avatar>;
    })();
    const headerTitle = firstCreator ? `@${firstCreator.username}` : firstYouTubeChannel?.channelName ?? label;
    const subtitleParts = [label];
    if (startDateParam && endDateParam) subtitleParts.push(`${startDateParam} – ${endDateParam}`);
    const subtitleLine = subtitleParts.join(' · ');

    return (
        <ShortFormDetailLayout
            onBack={handleBack}
            avatar={headerAvatar}
            title={headerTitle}
            subtitle={subtitleLine ? <Typography variant="body2" color="text.secondary">{subtitleLine}</Typography> : undefined}
            resumenTitle={isEnglish ? 'Summary' : 'Resumen'}
            cards={[
                { value: platformMetrics?.totalReels ?? 0, label: contentCountLabel },
                { value: platformMetrics?.totalViews ?? 0, label: 'Views' },
                { value: platformMetrics?.totalLikes ?? 0, label: 'Likes' },
                { value: platformMetrics?.totalComments ?? 0, label: isEnglish ? 'Comments' : 'Comentarios' },
            ]}
            formatNumber={formatNumber}
            contentTitle={label}
            contentChildren={
                effectivePlatform === 'youtube' && platformMentions.length > 0 ? (
                    (() => {
                        const deduped = Array.from(new Map(platformMentions.map((m: any) => [m.content?._id?.toString?.() ?? m.content?._id, m])).values());
                        return (
                            <TableContainer component={Paper} variant="outlined">
                                <Box
                                    sx={{
                                        display: { xs: 'flex', md: 'none' },
                                        alignItems: 'center',
                                        gap: 1,
                                        px: 2,
                                        py: 1,
                                        color: 'text.secondary',
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <SwipeLeftIcon />
                                    <Typography variant="caption">{isEnglish ? 'Swipe to see more' : 'Desliza para ver más'}</Typography>
                                </Box>
                                <Table sx={{ tableLayout: 'fixed' }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="left" sx={{ width: '40%', display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                {isEnglish ? 'Content' : 'Contenido'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                {isEnglish ? 'Published' : 'Published'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ display: { xs: 'table-cell', sm: 'none' }, padding: { xs: '12px', sm: '16px' } }}>
                                                Interactions
                                            </TableCell>
                                            <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                Views
                                            </TableCell>
                                            <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                Likes
                                            </TableCell>
                                            <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                {isEnglish ? 'Comments' : 'Comentarios'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ width: '50px', padding: { xs: '8px', sm: '12px' } }}>
                                                Link
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {deduped.map((m: any) => {
                                            const cId = m.content?._id?.toString?.();
                                            const content = m.content;
                                            const contentMentions = platformMentions.filter((x: any) => (x.content?._id?.toString?.() ?? x.content?._id) === cId);
                                            return (
                                                <TableRow
                                                    key={m._id}
                                                    hover
                                                    onClick={() => cId && content && handleContentClick(cId, content, contentMentions)}
                                                    sx={{ cursor: cId ? 'pointer' : 'default' }}
                                                >
                                                    <TableCell sx={{ width: '40%', overflow: 'hidden', display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }} align="left">
                                                        <Box sx={{ width: '100%', overflow: 'hidden' }}>
                                                            <Typography variant="body2" fontWeight="medium" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', display: 'block' }}>
                                                                {content?.title ?? '-'}
                                                            </Typography>
                                                            {m.context && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', display: 'block' }}>
                                                                    {m.context}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                        <Typography variant="caption">
                                                            {content?.publishedAt ? new Date(content.publishedAt).toLocaleDateString() : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ display: { xs: 'table-cell', sm: 'none' }, padding: { xs: '12px', sm: '16px' } }}>
                                                        <Typography variant="body2">{formatNumber((content?.likes ?? 0) + (content?.comments ?? 0))}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                        <Typography variant="body2">{formatNumber(content?.views ?? 0)}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                        <Typography variant="body2">{formatNumber(content?.likes ?? 0)}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                        <Typography variant="body2">{formatNumber(content?.comments ?? 0)}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ width: '50px', padding: { xs: '8px', sm: '12px' } }} onClick={(e) => e.stopPropagation()}>
                                                        {content?.url ? (
                                                            <IconButton size="small" onClick={() => window.open(content.url, '_blank')} sx={{ color: 'primary.main' }} title={isEnglish ? 'Open link' : 'Abrir enlace'}>
                                                                <OpenInNewIcon fontSize="small" />
                                                            </IconButton>
                                                        ) : (
                                                            <Typography variant="caption" color="text.disabled">{isEnglish ? 'No link' : 'Sin enlace'}</Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        );
                    })()
                ) : effectivePlatform === 'youtube' ? (
                    <Paper variant="section"><Typography color="text.secondary">{isEnglish ? 'No YouTube Shorts.' : 'No hay YouTube Shorts.'}</Typography></Paper>
                ) : creatorsData.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{isEnglish ? 'Creator' : 'Creador'}</TableCell>
                                    <TableCell align="right">{effectivePlatform === 'instagram' ? 'Reels' : 'Videos'}</TableCell>
                                    <TableCell align="right">Views</TableCell>
                                    <TableCell align="right">Likes</TableCell>
                                    <TableCell align="right">{isEnglish ? 'Comments' : 'Comentarios'}</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {creatorsData.map((creator) => (
                                    <TableRow key={creator.creatorId} hover onClick={() => handleCreatorClick(creator.creatorId)} sx={{ cursor: 'pointer' }}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {creator.profilePicS3Key ? <Avatar src={ensureDirectS3Url(getS3Url(creator.profilePicS3Key))} sx={{ width: 24, height: 24 }} /> : creator.profilePicUrl ? <Avatar src={creator.profilePicUrl} sx={{ width: 24, height: 24 }} /> : <Avatar sx={{ width: 24, height: 24 }}>{effectivePlatform === 'tiktok' ? <TikTokIcon fontSize="small" /> : <InstagramIcon fontSize="small" />}</Avatar>}
                                                <Typography variant="body2">@{creator.username}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right"><Chip label={creator.totalReels} size="small" /></TableCell>
                                        <TableCell align="right">{formatNumber(creator.totalViews)}</TableCell>
                                        <TableCell align="right">{formatNumber(creator.totalLikes)}</TableCell>
                                        <TableCell align="right">{formatNumber(creator.totalComments)}</TableCell>
                                        <TableCell><ChevronRightIcon fontSize="small" color="action" /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Paper variant="section">
                        <Typography color="text.secondary">{effectivePlatform === 'instagram' ? (isEnglish ? 'No Instagram Reels.' : 'No hay reels de Instagram.') : (isEnglish ? 'No TikTok videos.' : 'No hay videos de TikTok.')}</Typography>
                    </Paper>
                )}
        />
    );
};

export default BrandExplorerShortFormPlatformDetail;
